// src/actions/order_edit_actions.js
// Order-detail edits + fulfilment actions, against the commerce engine.
//
// Plain async helpers rather than redux thunks, deliberately. The order detail
// page (newOrder-details.js) owns three different data sources — shakerpro,
// saleor/mirror and the legacy US reader — and already refetches its own slice.
// Threading these through the admin reducer would couple this page to
// commerce_admin's state and give two components a claim on the same keys.
// Each helper returns { ok, code?, message? } and the caller refetches.
//
// THE ORDER NUMBER IS NOT THE ORDER ID. The page is keyed on the number
// (/analytics/saleor/order-details/2287 returns orderId: "2287"), while every
// admin mutation takes the engine gid, base64("Order:<uuid>"). resolveOrderGid
// bridges the two — staff-only, which is why the engine's by-number lookup had to
// accept a staff caller at all.
import { commerceGraphql } from "./commerce_admin_actions";

// Normalises the two failure shapes into one: a thrown transport/GraphQL error,
// and a 200 carrying { errors: [...] } in the payload.
async function run(query, variables, key) {
  try {
    const data = await commerceGraphql(query, variables);
    const payload = data && data[key];
    const errs = (payload && payload.errors) || [];
    if (errs.length) return { ok: false, code: errs[0].code || "ERROR", message: errs[0].message || "Save failed" };
    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, code: "TRANSPORT", message: e.message };
  }
}

// number -> gid. Returns null when the order is not in the engine, which is a real
// case worth distinguishing from an error: legacy US orders exist in the analytics
// database and have never been mirrored, and nothing on this page can edit those.
export async function resolveOrderGid(orderNumber) {
  const n = String(orderNumber || "").replace(/^BAR-[A-Z-]*/i, "").trim();
  if (!n) return null;
  try {
    const data = await commerceGraphql(
      `query($f:OrderFilterInput){ orders(first:1, filter:$f){ edges{ node{ id number status } } } }`,
      { f: { numbers: [n] } }
    );
    const edge = ((data.orders || {}).edges || [])[0];
    return edge ? edge.node : null;
  } catch {
    return null;
  }
}

const ADDR_FIELDS = `order{ number } errors{ code field message }`;

export const setShippingAddress = (gid, input) =>
  run(`mutation($id:ID!,$in:OrderAddressInput!){ orderShippingAddressSet(id:$id,input:$in){ ${ADDR_FIELDS} } }`,
    { id: gid, in: input }, "orderShippingAddressSet");

export const setBillingAddress = (gid, input) =>
  run(`mutation($id:ID!,$in:OrderAddressInput!){ orderBillingAddressSet(id:$id,input:$in){ ${ADDR_FIELDS} } }`,
    { id: gid, in: input }, "orderBillingAddressSet");

export const setCustomer = (gid, input) =>
  run(`mutation($id:ID!,$in:OrderCustomerInput!){ orderCustomerSet(id:$id,input:$in){ ${ADDR_FIELDS} } }`,
    { id: gid, in: input }, "orderCustomerSet");

export const setNote = (gid, note) =>
  run(`mutation($id:ID!,$n:String){ orderNoteSet(id:$id,note:$n){ ${ADDR_FIELDS} } }`,
    { id: gid, n: note }, "orderNoteSet");

// ---- fulfilment actions ----

export const cancelOrder = (gid) =>
  run(`mutation($id:ID!){ orderCancel(id:$id){ ${ADDR_FIELDS} } }`, { id: gid }, "orderCancel");

export const markRefunded = (gid) =>
  run(`mutation($id:ID!){ orderMarkAsRefunded(id:$id){ ${ADDR_FIELDS} } }`, { id: gid }, "orderMarkAsRefunded");

export const markPartiallyRefunded = (gid, amount) =>
  run(`mutation($id:ID!,$a:PositiveDecimal!){ orderMarkPartiallyRefunded(id:$id,amount:$a){ ${ADDR_FIELDS} } }`,
    { id: gid, a: amount }, "orderMarkPartiallyRefunded");

// FULFIL AND TRACK IS ONE ACTION, never two buttons.
//
// orderFulfill alone sends no shipping email and does NOT release the PayPal hold —
// both live in orderFulfillmentUpdateTracking. An operator who fulfilled and
// stopped had shipped the goods while the buyer's funds stayed held, with nothing
// saying so. The same reasoning already governs the admin Orders page
// (commerce_admin_actions.js fulfillAndTrack); this is the order-detail page's
// equivalent, and it needs the fulfilment id that only the fulfil response carries.
export async function fulfilAndTrack(gid, trackingNumber, carrier) {
  try {
    const data = await commerceGraphql(
      `mutation($id:ID!){ orderFulfill(order:$id){ order{ fulfillments{ id trackingNumber } } errors{ code message } } }`,
      { id: gid }
    );
    const p = data.orderFulfill || {};
    if ((p.errors || []).length) {
      const e = p.errors[0];
      return { ok: false, code: e.code, message: `${e.code}: ${e.message}` };
    }
    const fulfilments = ((p.order || {}).fulfillments) || [];
    const target = fulfilments.find((f) => !f.trackingNumber) || fulfilments[fulfilments.length - 1];
    if (!target) {
      // Fulfilled, but tracking cannot be attached — say exactly what that means
      // rather than reporting a clean success.
      return { ok: false, code: "NO_FULFILMENT",
        message: "Marked fulfilled, but no fulfilment came back, so tracking was not attached — the customer was not emailed and the PayPal hold is NOT released." };
    }
    const t = await commerceGraphql(
      `mutation($id:ID!,$in:TrackingInput!){ orderFulfillmentUpdateTracking(id:$id,input:$in){ errors{ code message } } }`,
      { id: target.id, in: carrier ? { trackingNumber, carrier } : { trackingNumber } }
    );
    const terrs = ((t.orderFulfillmentUpdateTracking || {}).errors) || [];
    if (terrs.length) {
      // PARTIAL SUCCESS. The goods ARE recorded as fulfilled; only the step that
      // emails the customer and releases the hold failed, so this must not read as
      // a total failure.
      return { ok: false, code: terrs[0].code, fulfilled: true,
        message: `Fulfilled, but tracking failed (${terrs[0].code}: ${terrs[0].message}) — the customer was not emailed and the PayPal hold is NOT released.` };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, code: "TRANSPORT", message: e.message };
  }
}
